import { useEffect, useState } from 'react';
import useWorldStore from '../store/worldStore';
import { ZONES } from '../data/zones';
import { ACTIVITIES } from '../simulation/activityConfig';
import { getRecentMemoryObjects } from '../services/agentMemory';
import { patchWorldAgent } from '../../../lib/backend/world';
import { getErrorMessage, showErrorToast } from '../../../lib/toast-store';
import {
  buildChatAppCharacterProfile,
  responseMaxCharactersForVerbosity,
} from '../lib/characterConfig';
import {
  CHAT_APP_MODEL_KEYS,
  getChatAppAppearanceModel,
} from '../lib/chatAppCharacters';
import { deriveTags, deriveTrait } from '../lib/deriveTags';
import { deriveStats } from '../lib/deriveStats';
import CharacterHero from './character/CharacterHero';
import PersonaSection from './character/PersonaSection';
import PersonaFilesEditor from './character/PersonaFilesEditor';
import DirectivesSection from './character/DirectivesSection';
import TagList from './character/TagList';
import StatReadout from './character/StatReadout';
import SkillGrid from './character/SkillGrid';
import { SkillGrid as LoadoutSkillGrid } from '../../../components/skill-grid';
import { buildAutoSkillGridLayout } from '../../../lib/grid-logic';
import TasksRail from './character/TasksRail';
import {
  mono,
  sectionHeaderStyle,
  secondaryButtonStyle,
} from './character/tokens';
import ConfirmModal from './character/ConfirmModal';
import useWorldDeleteAgent from '../hooks/useWorldDeleteAgent';

function getInitialCharacterForm(agent) {
  return {
    name: agent?.characterConfig?.name || agent?.name || '',
    personality_prompt: agent?.characterConfig?.personality_prompt || '',
    backstory_prompt: agent?.characterConfig?.backstory_prompt || '',
    quirks_and_mannerisms: agent?.characterConfig?.quirks_and_mannerisms || '',
    beliefs_and_intents: agent?.characterConfig?.beliefs_and_intents || '',
    verbosity: agent?.characterConfig?.verbosity || 'medium',
    response_max_characters: agent?.characterConfig?.response_max_characters || 400,
  };
}

function buildCharacterSaveFailure(error) {
  const fallbackDetail =
    error instanceof Error ? error.message : 'Character save failed.';
  const status =
    error && typeof error === 'object' && typeof error.status === 'number'
      ? error.status
      : null;
  const detail =
    error && typeof error === 'object' && typeof error.detail === 'string' && error.detail.trim()
      ? error.detail.trim()
      : fallbackDetail;

  let failureMode = 'Unknown failure';
  let summary = 'The character profile could not be saved.';

  if (
    detail.includes('Missing character-platform session') ||
    status === 401 ||
    status === 403
  ) {
    failureMode = 'Session unavailable';
    summary = 'You are not currently authenticated with character-platform.';
  } else if (
    status === 503 ||
    detail.includes('MongoDB is not configured for world state') ||
    detail.includes('workspace backend failed to initialize')
  ) {
    failureMode = 'Backend unavailable';
    summary = 'The world backend is unavailable, so profile edits cannot be persisted.';
  } else if (status === 404 || detail.includes('OpenClaw agent not found')) {
    failureMode = 'Agent missing';
    summary = 'The backend could not find this OpenClaw agent for your account.';
  } else if (status === 400) {
    failureMode = 'Validation rejected';
    summary = 'The backend rejected the profile update payload.';
  } else if (detail.toLowerCase().includes('timed out')) {
    failureMode = 'Request timeout';
    summary = 'The save request timed out before the backend confirmed the update.';
  }

  return {
    detail,
    failureMode,
    status,
    summary,
    title: 'Character Save Failed',
  };
}

export default function AgentPanel() {
  const selectedAgentId = useWorldStore((s) => s.selectedAgentId);
  const agents = useWorldStore((s) => s.agents);
  const deselectAgent = useWorldStore((s) => s.deselectAgent);
  const openChat = useWorldStore((s) => s.openChat);
  const startCall = useWorldStore((s) => s.startCall);
  const activeTasks = useWorldStore((s) => s.activeTasks);
  const taskQueues = useWorldStore((s) => s.taskQueues);
  const completedTasks = useWorldStore((s) => s.completedTasks);
  const openResultModal = useWorldStore((s) => s.openResultModal);
  const updateAgentState = useWorldStore((s) => s.updateAgentState);
  const { deleteAgentFromWorld, getDeleteState, deleteErrorMessage } = useWorldDeleteAgent();

  const [tab, setTab] = useState('profile');
  const [isEditingCharacter, setIsEditingCharacter] = useState(false);
  const [isSavingCharacter, setIsSavingCharacter] = useState(false);
  const [characterSaveError, setCharacterSaveError] = useState(null);
  const [characterForm, setCharacterForm] = useState(() => getInitialCharacterForm(null));
  const [isSavingDirectives, setIsSavingDirectives] = useState(false);
  const [directivesSaveError, setDirectivesSaveError] = useState(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const agent = agents.find((a) => a.id === selectedAgentId);
  const deleteState = agent ? getDeleteState(agent.id) : 'idle';
  const zone = agent ? ZONES.find((z) => z.id === agent.currentZoneId) : null;
  const agentChatAppModel = agent?.profileDoc?.chat_app_model || 'lobster';
  const savedAppearance = getChatAppAppearanceModel(agentChatAppModel);
  const color = zone?.color || savedAppearance.accent;

  const characterConfig = agent?.characterConfig || {};
  const derivedTags = agent ? deriveTags(characterConfig) : [];
  const derivedTrait = agent ? deriveTrait(characterConfig) : null;
  const derivedStats = agent ? (agent.stats || deriveStats(characterConfig, agent.id)) : null;

  useEffect(() => {
    if (!selectedAgentId) return;
    setIsEditingCharacter(false);
    setCharacterSaveError(null);
    setCharacterForm(getInitialCharacterForm(agent));
  }, [selectedAgentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the backend (re)generates characterConfig while the panel is open —
  // e.g. the initial post-install /world/import finishes after the panel
  // mounted — sync the non-editing form values so the UI shows the new data.
  // Skip while the user is actively editing to avoid clobbering their typing.
  useEffect(() => {
    if (!selectedAgentId || !agent || isEditingCharacter) return;
    setCharacterForm(getInitialCharacterForm(agent));
  }, [
    agent,
    selectedAgentId,
    isEditingCharacter,
    agent?.characterConfig?.personality_prompt,
    agent?.characterConfig?.backstory_prompt,
    agent?.characterConfig?.quirks_and_mannerisms,
    agent?.characterConfig?.beliefs_and_intents,
    agent?.characterConfig?.verbosity,
    agent?.characterConfig?.response_max_characters,
    agent?.characterConfig?.name,
  ]);

  if (!agent) return null;

  const cycleModel = (delta) => {
    const currentIndex = CHAT_APP_MODEL_KEYS.indexOf(savedAppearance.key);
    const nextIndex =
      (currentIndex + delta + CHAT_APP_MODEL_KEYS.length) % CHAT_APP_MODEL_KEYS.length;
    const nextKey = CHAT_APP_MODEL_KEYS[nextIndex];
    const nextProfile = {
      ...(agent.profileDoc || {}),
      chat_app_model: nextKey,
    };
    updateAgentState(agent.id, { profileDoc: nextProfile });
    patchWorldAgent(agent.id, { profile: nextProfile }).catch((err) => {
      console.warn('Failed to persist chat_app_model change:', err);
      showErrorToast({
        id: `chat-model-save-failed-${agent.id}`,
        message: `Chat model save failed for ${agent.name}: ${getErrorMessage(err, 'Backend save failed')}`,
      });
    });
  };

  const openclawSkills = Array.isArray(agent.openclawSkills) ? agent.openclawSkills : [];

  const activity = agent.activity ? ACTIVITIES[agent.activity] : null;
  const taskDisplay = activity
    ? `${activity.emoji} ${activity.label}`
    : agent.currentTask || 'Standby';

  const handleEnterEdit = () => {
    setCharacterForm(getInitialCharacterForm(agent));
    setIsEditingCharacter(true);
    setCharacterSaveError(null);
  };

  const handleCancelEdit = () => {
    setIsEditingCharacter(false);
    setCharacterSaveError(null);
    setCharacterForm(getInitialCharacterForm(agent));
  };

  const handleSaveCharacter = async () => {
    setIsSavingCharacter(true);
    setCharacterSaveError(null);
    try {
      const nextCharacterConfig = {
        ...agent.characterConfig,
        ...characterForm,
      };
      const nextProfile = {
        ...(agent.profileDoc || {}),
        character_config: nextCharacterConfig,
      };

      const result = await patchWorldAgent(agent.id, { profile: nextProfile });
      const savedProfile =
        result?.profile && typeof result.profile === 'object' ? result.profile : nextProfile;
      const savedCharacterConfig = savedProfile.character_config || nextCharacterConfig;
      const finalConfig = {
        ...savedCharacterConfig,
        response_max_characters: responseMaxCharactersForVerbosity(savedCharacterConfig.verbosity),
      };

      updateAgentState(agent.id, {
        name: finalConfig.name,
        openclawAgentName: finalConfig.name,
        characterConfig: finalConfig,
        stats: deriveStats(finalConfig, agent.id),
        personality: {
          ...(agent.personality || {}),
          trait: deriveTrait(finalConfig),
          tags: deriveTags(finalConfig),
        },
        chatAppCharacterId:
          savedProfile.chat_app_character_id || agent.chatAppCharacterId || null,
        chatAppCharacterProfile:
          savedProfile.chat_app_character_profile || buildChatAppCharacterProfile(finalConfig),
        profileDoc: savedProfile,
      });
      setIsEditingCharacter(false);
    } catch (error) {
      setCharacterSaveError(buildCharacterSaveFailure(error));
    } finally {
      setIsSavingCharacter(false);
    }
  };

  const handleSaveDirectives = async ({ autonomyLevel, directives }) => {
    if (!agent) return;
    setIsSavingDirectives(true);
    setDirectivesSaveError(null);
    try {
      const nextProfile = {
        ...(agent.profileDoc || {}),
        autonomyLevel,
        directives,
      };
      const result = await patchWorldAgent(agent.id, { profile: nextProfile });
      const savedProfile =
        result?.profile && typeof result.profile === 'object' ? result.profile : nextProfile;
      updateAgentState(agent.id, {
        autonomyLevel:
          typeof savedProfile.autonomyLevel === 'number'
            ? savedProfile.autonomyLevel
            : autonomyLevel,
        directives: savedProfile.directives || directives,
        profileDoc: savedProfile,
      });
    } catch (error) {
      const detail =
        error && typeof error === 'object' && typeof error.detail === 'string' && error.detail.trim()
          ? error.detail.trim()
          : error instanceof Error
            ? error.message
            : 'Failed to save directives.';
      setDirectivesSaveError(detail);
      throw error;
    } finally {
      setIsSavingDirectives(false);
    }
  };

  const handleCharacterConfigRegenerated = (regeneratedConfig) => {
    if (!agent || !regeneratedConfig) return;
    const finalConfig = {
      ...regeneratedConfig,
      response_max_characters:
        regeneratedConfig.response_max_characters ||
        responseMaxCharactersForVerbosity(regeneratedConfig.verbosity),
    };
    updateAgentState(agent.id, {
      name: finalConfig.name || agent.name,
      openclawAgentName: finalConfig.name || agent.openclawAgentName,
      characterConfig: finalConfig,
      stats: deriveStats(finalConfig, agent.id),
      personality: {
        ...(agent.personality || {}),
        trait: deriveTrait(finalConfig),
        tags: deriveTags(finalConfig),
      },
      chatAppCharacterProfile: buildChatAppCharacterProfile(finalConfig),
      profileDoc: {
        ...(agent.profileDoc || {}),
        character_config: finalConfig,
        character_config_source: 'openclaw_identity_files',
      },
    });
  };

  const heroActions = (
    <div style={{ display: 'flex', gap: 4 }}>
      <button
        onClick={() => {
          openChat(agent.id);
          deselectAgent();
        }}
        style={{
          flex: 1,
          height: 26,
          ...mono,
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          background: `${color}15`,
          border: `1px solid ${color}55`,
          color: `${color}cc`,
          borderRadius: 4,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
        }}
      >
        <span style={{ fontSize: 11, lineHeight: 1 }}>💬</span>
        <span>Chat</span>
      </button>
      <button
        onClick={() => {
          startCall(agent.id);
          deselectAgent();
        }}
        style={{
          flex: 1,
          height: 26,
          ...mono,
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          background: 'none',
          border: '1px solid rgba(255,255,255,0.15)',
          color: 'rgba(255,255,255,0.55)',
          borderRadius: 4,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
        }}
      >
        <span style={{ fontSize: 11, lineHeight: 1 }}>📞</span>
        <span>Call</span>
      </button>
    </div>
  );

  const profileBody = (
    <div style={{ display: 'grid', gap: 14, minWidth: 0 }}>
      <PersonaSection
        mode={isEditingCharacter ? 'edit' : 'view'}
        values={isEditingCharacter ? characterForm : characterConfig}
        onChange={setCharacterForm}
        onEnterEdit={handleEnterEdit}
        onSave={handleSaveCharacter}
        onCancel={handleCancelEdit}
        saving={isSavingCharacter}
        color={color}
        modelLabel={`${savedAppearance.icon} ${savedAppearance.label}`}
      />

      {derivedTags.length > 0 && (
        <div>
          <div style={{ ...sectionHeaderStyle, marginBottom: 8 }}>Tags</div>
          <TagList tags={derivedTags} color={color} />
        </div>
      )}

      <div>
        <div style={{ ...sectionHeaderStyle, marginBottom: 8 }}>Stats</div>
        <StatReadout stats={derivedStats} color={color} />
      </div>

      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <div style={sectionHeaderStyle}>Skills ({openclawSkills.length})</div>
          {openclawSkills.length > 0 && (
            <button
              onClick={() => setTab('skills')}
              style={{
                ...secondaryButtonStyle,
                height: 22,
                padding: '0 8px',
              }}
            >
              View all
            </button>
          )}
        </div>
        <SkillGrid
          skills={openclawSkills}
          variant="compact"
          maxCompact={8}
          onShowMore={() => setTab('skills')}
          emptyLabel="No skills synced yet"
        />
      </div>

      <RecentActivity agentId={agent.id} />
    </div>
  );

  const personaBody = (
    <div style={{ display: 'grid', gap: 18, minWidth: 0 }}>
      <DirectivesSection
        agent={agent}
        color={color}
        saving={isSavingDirectives}
        saveError={directivesSaveError}
        onSave={handleSaveDirectives}
      />
      <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
      <PersonaFilesEditor
        agentId={agent.id}
        active={tab === 'persona'}
        color={color}
        onCharacterConfigRegenerated={handleCharacterConfigRegenerated}
        seededFiles={agent.personaFilesSeed || null}
      />
    </div>
  );

  const equippedSkillRows = openclawSkills.filter(
    (s) => s.enabled !== false && s.slug,
  );
  const skillSlugs = equippedSkillRows.map((s) => s.slug);
  const gridLayout = buildAutoSkillGridLayout(equippedSkillRows);
  const skillInfoMap = new Map(
    equippedSkillRows.map((s) => [
      s.slug,
      {
        name: s.name || s.slug,
        description: s.description || '',
        category: s.category,
        skillMdSize: s.skill_md_size ?? null,
      },
    ]),
  );

  const skillsBody = (
    <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={sectionHeaderStyle}>Skill Loadout</div>
          {openclawSkills.length > 0 && (
            <div style={{ ...mono, fontSize: 10, color: '#4ade80' }}>
              {skillSlugs.length} of {openclawSkills.length} enabled
            </div>
          )}
        </div>
        <a
          href="/skills"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            ...secondaryButtonStyle,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 26,
          }}
        >
          <span style={{ fontSize: 13, lineHeight: 1 }}>＋</span>
          <span>Add more skills</span>
        </a>
      </div>
      <div
        style={{
          padding: 18,
          borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.02)',
        }}
      >
        <LoadoutSkillGrid
          skills={skillSlugs}
          gridLayout={gridLayout}
          initialSkillInfo={skillInfoMap}
          size="lg"
          previewVariant="detail"
          emptyMessage="No skills equipped. Click 'Add more skills' to browse the catalog."
        />
      </div>
    </div>
  );

  return (
    <>
      <div
        onClick={deselectAgent}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 100,
        }}
      />
      <style>{`
        @keyframes modalIn { from { opacity:0; transform:translate(-50%,-50%) scale(0.96); } to { opacity:1; transform:translate(-50%,-50%) scale(1); } }
      `}</style>

      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 1280,
          maxWidth: '92vw',
          height: 640,
          maxHeight: '88vh',
          background: 'rgba(6,6,7,0.95)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${color}30`,
          borderRadius: 12,
          zIndex: 101,
          overflow: 'hidden',
          boxShadow: 'var(--shadow-glass)',
          animation: 'modalIn 0.25s cubic-bezier(0.22,1,0.36,1)',
          display: 'flex',
        }}
      >
        <button
          onClick={deselectAgent}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 8,
            right: 10,
            width: 28,
            height: 28,
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.5)',
            fontSize: 20,
            lineHeight: 1,
            cursor: 'pointer',
            zIndex: 4,
            ...mono,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
        >
          ×
        </button>

        {/* Column 1 — Hero */}
        <div style={{ flex: '0 0 150px', display: 'flex', flexDirection: 'column' }}>
          <CharacterHero
            modelKey={savedAppearance.key}
            onPrevModel={() => cycleModel(-1)}
            onNextModel={() => cycleModel(1)}
            name={agent.name}
            role={agent.role}
            trait={derivedTrait}
            subtitle={taskDisplay}
            color={color}
            actions={heroActions}
            onDelete={() => setIsConfirmingDelete(true)}
          />
        </div>

        {/* Column 2 — Main */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              flexShrink: 0,
            }}
          >
            {[
              { id: 'profile', label: 'Profile' },
              { id: 'persona', label: 'Agent' },
              { id: 'skills', label: `Skills (${openclawSkills.length})` },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  ...mono,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  background: 'none',
                  border: 'none',
                  color: tab === t.id ? 'white' : 'rgba(255,255,255,0.35)',
                  borderBottom:
                    tab === t.id ? `2px solid ${color}` : '2px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '16px 20px',
              minWidth: 0,
            }}
          >
            {tab === 'profile' ? profileBody : tab === 'persona' ? personaBody : skillsBody}
          </div>
        </div>

        {/* Column 3 — Tasks rail */}
        <TasksRail
          agentId={agent.id}
          activeTask={activeTasks[agent.id]}
          queued={taskQueues[agent.id] || []}
          completed={completedTasks}
          onOpenCompleted={openResultModal}
          color={color}
        />
      </div>

      <ConfirmModal
        open={isConfirmingDelete}
        title="Permanently delete this agent?"
        description={`${agent.name} and all of their tasks, chats, and runtime state will be removed from your world. This cannot be undone.`}
        errorMessage={deleteState === 'error' ? deleteErrorMessage : null}
        confirmLabel={deleteState === 'loading' ? 'Deleting…' : 'Delete agent'}
        tone="destructive"
        onCancel={() => setIsConfirmingDelete(false)}
        onConfirm={async () => {
          if (deleteState === 'loading') return;
          const deleted = await deleteAgentFromWorld(agent);
          if (deleted) setIsConfirmingDelete(false);
        }}
      />

      {characterSaveError ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 102,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={() => setCharacterSaveError(null)}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.52)',
            }}
          />
          <div
            style={{
              position: 'relative',
              width: 'min(460px, calc(100vw - 32px))',
              borderRadius: 8,
              background: 'rgba(8,10,16,0.96)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(248,113,113,0.24)',
              boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
              padding: 18,
              display: 'grid',
              gap: 12,
              animation: 'modalIn 0.2s cubic-bezier(0.22,1,0.36,1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ display: 'grid', gap: 4 }}>
                <div
                  style={{
                    ...mono,
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'rgba(255,240,240,0.96)',
                  }}
                >
                  {characterSaveError.title}
                </div>
                <div
                  style={{
                    fontFamily: '"DM Mono", monospace',
                    fontSize: 9,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#fca5a5',
                  }}
                >
                  Failure Mode: {characterSaveError.failureMode}
                </div>
              </div>
              <button
                onClick={() => setCharacterSaveError(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.45)',
                  fontSize: 16,
                  cursor: 'pointer',
                  ...mono,
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                ...mono,
                fontSize: 12,
                lineHeight: 1.6,
                color: 'rgba(255,255,255,0.76)',
              }}
            >
              {characterSaveError.summary}
            </div>

            <div
              style={{
                display: 'grid',
                gap: 8,
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(255,255,255,0.03)',
              }}
            >
              {characterSaveError.status ? (
                <div
                  style={{
                    fontFamily: '"DM Mono", monospace',
                    fontSize: 9,
                    color: 'rgba(255,255,255,0.52)',
                  }}
                >
                  HTTP Status: {characterSaveError.status}
                </div>
              ) : null}
              <div
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: 9,
                  lineHeight: 1.6,
                  color: '#fecaca',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {characterSaveError.detail}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setCharacterSaveError(null)}
                style={{
                  ...secondaryButtonStyle,
                  minWidth: 104,
                }}
              >
                Keep Editing
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function RecentActivity({ agentId }) {
  const memories = getRecentMemoryObjects(agentId, 4);
  return (
    <div>
      <div style={{ ...sectionHeaderStyle, marginBottom: 8 }}>Recent Activity</div>
      {memories.length === 0 ? (
        <div style={{ ...mono, fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>
          No activity yet
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 4 }}>
          {memories.map((m) => {
            const age = (() => {
              const s = Math.floor((Date.now() - m.timestamp) / 1000);
              if (s < 60) return `${s}s`;
              if (s < 3600) return `${Math.floor(s / 60)}m`;
              return `${Math.floor(s / 3600)}h`;
            })();
            return (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  gap: 8,
                  ...mono,
                  fontSize: 10,
                  padding: '5px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <span style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0, width: 28 }}>{age}</span>
                <span
                  style={{
                    color: 'rgba(255,255,255,0.55)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {m.content}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
